import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Button, Paper, Dialog, DialogActions, DialogContent, DialogTitle,
  Tooltip, Typography, Chip, MenuItem, TextField, Menu,
  IconButton, ListItemText, ListItemIcon, Checkbox,
  Popover, Badge, Divider
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import ExportButton from './BotonEx';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloseIcon from '@mui/icons-material/Close';
import FilterListAltIcon from '@mui/icons-material/FilterListAlt';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SyncDialog from './SyncDialog';

function normalizeEmpresaScopeInput(value) {
  if (value == null || value === '' || value === 'null') return '0';

  const parseArray = (arr) => {
    const normalized = arr
      .map((x) => Number(String(x).trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);

    return normalized.length ? normalized.join(',') : '0';
  };

  if (Array.isArray(value)) {
    return parseArray(value);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? String(value) : '0';
  }

  if (typeof value === 'string') {
    const s = value.trim();

    if (!s) return '0';

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parseArray(parsed);
        }
      } catch { }
    }

    if (s.includes(',')) {
      return parseArray(s.split(','));
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? String(n) : '0';
  }

  return '0';
}

function FilterComponent({
  handleImport,
  setExcelData,
  importar,
  data,
  filterData,
  setFilterData,
  handleClick,
  handleEnterKeyPress,
  showFilterButton = true,
  showImportButton = true,
  showExportButton = true,
  initialFilters = [],
  estatusOptions = [],
  importMode = 'excel',
  showHeaderActions = true,

  // ✅ Persistencia
  persistEnabled = false,
  persistKey = '',
  persistVista = 'filters',
  usuarioId = null,

  // ✅ nuevo
  empresaScope = '0',

  // ✅ compatibilidad hacia atrás
  empresaId = 0,

  // ✅ SyncDialog: props opcionales para el dialog dedicado de sincronización
  syncDialogProps = {},

  apiUrl = '',
}) {

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('No se ha seleccionado archivo.');
  const [isFileProcessed, setIsFileProcessed] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // ✅ Estabilizar initialFilters: si el padre pasa un array literal inline en cada render,
  // su referencia cambia siempre. Usamos useMemo con JSON como clave de identidad
  // para evitar que los useEffect dependientes se disparen en bucle infinito.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableInitialFilters = useMemo(() => initialFilters, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(initialFilters)
  ]);

  // ===================== ✅ Scope estable de empresas =====================
  // Prioridad:
  // 1) empresaScope nuevo
  // 2) empresaId viejo
  const normalizedEmpresaScope = useMemo(() => {
    const source = empresaScope != null && empresaScope !== '' ? empresaScope : empresaId;
    return normalizeEmpresaScopeInput(source);
  }, [empresaScope, empresaId]);

  const canPersist = Boolean(persistEnabled && apiUrl && persistKey && usuarioId != null);

  // ✅ comboKey estable por usuario + scope + modulo + vista
  const comboKey = useMemo(() => {
    const mod = persistKey || 'default';
    const vista = persistVista || 'filters';
    const u = Number(usuarioId || 0);
    const scope = normalizedEmpresaScope || '0';
    return `${mod}|${vista}|u:${u}|scope:${scope}`;
  }, [persistKey, persistVista, usuarioId, normalizedEmpresaScope]);

  // ✅ localStorage key POR usuario+scope+modulo+vista
  const VISIBLE_FILTERS_KEY = useMemo(() => {
    return `filterComponent.visibleFilters.${comboKey}`;
  }, [comboKey]);

  const readVisibleFromLocal = useCallback(() => {
    try {
      const saved = localStorage.getItem(VISIBLE_FILTERS_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [VISIBLE_FILTERS_KEY]);

  // ===================== ✅ Estado visibleFilters =====================
  const [visibleFilters, setVisibleFilters] = useState(() => {
    const local = (() => {
      try {
        const saved = localStorage.getItem(VISIBLE_FILTERS_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    })();

    if (local) {
      // ✅ Unimos la config guardada con los filtros iniciales para no perder campos nuevos.
      return [...new Set([...local, ...initialFilters])];
    }
    return initialFilters;
  });

  // ===================== ✅ Control de hidratación + anti-race =====================
  const [hydrated, setHydrated] = useState(false);

  const loadReqIdRef = useRef(0);
  const lastHydratedKeyRef = useRef('');
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef(null);

  // ✅ al cambiar combo: cancelar timers, reset flags, setear visible inmediato
  // NOTA: stableInitialFilters en deps en lugar de initialFilters para evitar
  // loop infinito cuando el padre pasa un array literal nuevo en cada render.
  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    setHydrated(false);
    dirtyRef.current = false;
    lastHydratedKeyRef.current = '';

    const local = readVisibleFromLocal();
    setVisibleFilters(local ? [...new Set([...local, ...stableInitialFilters])] : stableInitialFilters);
  }, [comboKey, stableInitialFilters, readVisibleFromLocal]);

  // ✅ guardar en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(VISIBLE_FILTERS_KEY, JSON.stringify(visibleFilters));
    } catch { }
  }, [visibleFilters, VISIBLE_FILTERS_KEY]);

  // ===================== ✅ Carga desde BD =====================
  const loadPrefsFromDB = useCallback(async () => {
    if (!canPersist) {
      setHydrated(true);
      lastHydratedKeyRef.current = comboKey;
      return;
    }

    const reqId = ++loadReqIdRef.current;

    try {
      const params = {
        usuarioId,
        modulo: persistKey,
        vista: persistVista,
      };

      // ✅ usa scope nuevo; deja empresaId por compatibilidad
      if (normalizedEmpresaScope && normalizedEmpresaScope !== '0') {
        params.empresaScope = normalizedEmpresaScope;
      } else {
        params.empresaScope = '0';
        params.empresaId = 0;
      }

      const res = await axios.get(`${apiUrl}/api/userPrefs`, { params });

      if (reqId !== loadReqIdRef.current) return;

      const prefRaw = res?.data?.pref;
      if (!prefRaw) {
        setHydrated(true);
        lastHydratedKeyRef.current = comboKey;
        return;
      }

      let prefObj = null;
      try {
        prefObj = typeof prefRaw === 'string' ? JSON.parse(prefRaw) : prefRaw;
      } catch {
        prefObj = null;
      }

      if (prefObj && Array.isArray(prefObj.visibleFilters)) {
        // ✅ También unimos al cargar desde la base de datos
        setVisibleFilters(prev => [...new Set([...prefObj.visibleFilters, ...stableInitialFilters])]);
      }

      setHydrated(true);
      lastHydratedKeyRef.current = comboKey;
    } catch (e) {
      if (reqId !== loadReqIdRef.current) return;
      console.error('❌ Error cargando prefs (visibleFilters):', e);
      setHydrated(true);
      lastHydratedKeyRef.current = comboKey;
    }
  }, [canPersist, apiUrl, usuarioId, normalizedEmpresaScope, persistKey, persistVista, comboKey, stableInitialFilters]);

  useEffect(() => {
    loadPrefsFromDB();
  }, [loadPrefsFromDB]);

  // ===================== ✅ Guardado a BD =====================
  const savePrefsToDB = useCallback(async () => {
    if (!canPersist) return;
    if (!hydrated) return;
    if (lastHydratedKeyRef.current !== comboKey) return;
    if (!dirtyRef.current) return;

    try {
      const payload = {
        usuarioId,
        modulo: persistKey,
        vista: persistVista,
        pref: {
          visibleFilters,
          updatedAt: new Date().toISOString(),
        }
      };

      // ✅ usa scope nuevo; deja empresaId por compatibilidad
      if (normalizedEmpresaScope && normalizedEmpresaScope !== '0') {
        payload.empresaScope = normalizedEmpresaScope;
      } else {
        payload.empresaScope = '0';
        payload.empresaId = 0;
      }

      await axios.post(`${apiUrl}/api/userPrefs`, payload);

      dirtyRef.current = false;
    } catch (e) {
      console.error('❌ Error guardando prefs (visibleFilters):', e);
    }
  }, [canPersist, hydrated, comboKey, apiUrl, usuarioId, normalizedEmpresaScope, persistKey, persistVista, visibleFilters]);

  // debounce autosave
  useEffect(() => {
    if (!canPersist) return;
    if (!hydrated) return;
    if (lastHydratedKeyRef.current !== comboKey) return;
    if (!dirtyRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      savePrefsToDB();
    }, 450);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [visibleFilters, canPersist, hydrated, comboKey, savePrefsToDB]);

  // ===================== Excel import =====================
  const MAX_FILE_SIZE = 15 * 1024 * 1024;
  const allowedExtensions = ['.xls', '.xlsx'];
  const allowedMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];

  const validateFile = (file) => {
    if (!file) return false;
    const ext = `.${(file.name?.split('.').pop() || '').toLowerCase()}`;
    if (!allowedExtensions.includes(ext) || !allowedMimeTypes.includes(file.type)) {
      setSelectedFileName('Por favor, selecciona un archivo Excel válido (.xls o .xlsx).');
      setExcelData?.([]);
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setSelectedFileName(`El archivo excede ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB.`);
      setExcelData?.([]);
      return false;
    }
    return true;
  };

  const readExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const dataArr = new Uint8Array(e.target.result);
        const workbook = XLSX.read(dataArr, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
        setExcelData?.(sheetData);
        setSelectedFileName(file.name);
        setIsFileProcessed(true);
      } catch (error) {
        console.error('Error al leer el archivo Excel:', error);
        setSelectedFileName('Hubo un error al leer el archivo Excel.');
        setExcelData?.([]);
        setIsFileProcessed(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!validateFile(file)) return;
    readExcelFile(file);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxSize: MAX_FILE_SIZE,
  });

  const handleCloseModal = () => {
    requestAnimationFrame(() => {
      setModalOpen(false);
      setExcelData?.([]);
      setSelectedFileName('No se ha seleccionado archivo.');
      setIsFileProcessed(false);
    });
  };

  // ===================== Filtros UI =====================
  const handleDateChangeNative = (value, key) => {
    setFilterData((prev) => ({ ...(prev || {}), [key]: value || '' }));
  };

  const toggleFilter = useCallback((key) => {
    dirtyRef.current = true;

    setVisibleFilters((prev) => {
      const has = prev.includes(key);
      return has ? prev.filter((k) => k !== key) : [...prev, key];
    });
  }, []);

  const clearAllFilters = () => {
    const cleared = Object.keys(filterData || {}).reduce((acc, k) => ({ ...acc, [k]: '' }), {});
    setFilterData(cleared);
    if (typeof handleClick === 'function') handleClick(cleared);
  };

  const submitFilters = () => {
    if (typeof handleClick === 'function') handleClick(filterData);
  };

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const activeFiltersCount = useMemo(() => {
    return Object.values(filterData || {}).filter((v) => v !== '' && v != null).length;
  }, [filterData]);

  const openAdvanced = Boolean(filterAnchorEl);

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* ── Slim Header Bar ── */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 0.75,
          borderRadius: 3,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        {/* Toggle Advanced Filters Button */}
        <Badge
          badgeContent={activeFiltersCount}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              right: 2,
              top: 2,
              border: `2px solid #fff`,
              padding: '0 4px',
              height: 18,
              minWidth: 18,
              fontSize: '0.65rem'
            }
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{
              height: 32,
              borderRadius: 2,
              borderColor: activeFiltersCount > 0 ? '#0655A5' : '#e5e7eb',
              color: activeFiltersCount > 0 ? '#0655A5' : 'text.secondary',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.75rem',
              px: 1.5,
              '&:hover': {
                borderColor: '#0655A5',
                bgcolor: 'rgba(6, 85, 165, 0.04)'
              }
            }}
          >
            Filtros
          </Button>
        </Badge>
        
        {/* Quick Search Input */}
        <TextField
          size="small"
          placeholder="Búsqueda rápida..."
          value={filterData?.globalSearch || ''}
          onChange={(e) => {
            const val = e.target.value;
            setFilterData(prev => ({ ...prev, globalSearch: val }));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitFilters();
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 18 }} />,
            sx: { 
              height: 32, 
              fontSize: '0.75rem', 
              borderRadius: 2,
              bgcolor: '#f1f5f9',
              border: 'none',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#e2e8f0' },
              '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 2px rgba(6,85,165,0.2)' }
            }
          }}
          sx={{ width: { xs: 150, sm: 240 }, ml: 1 }}
        />

        {/* Separator */}
        <Divider orientation="vertical" flexItem sx={{ height: 20, my: 'auto', borderColor: '#e5e7eb', mx: 0.5 }} />

        {/* Horizontal Active Chips */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          gap: 0.75,
          alignItems: 'center',
          overflowX: 'auto',
          py: 0.25,
          '&::-webkit-scrollbar': { display: 'none' }, // Hide scrollbar but keep scrollability
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          {activeFiltersCount === 0 ? (
            <Typography sx={{ fontSize: '0.73rem', color: 'text.disabled', fontStyle: 'italic', ml: 0.5 }}>
              Ningún filtro aplicado
            </Typography>
          ) : (
            Object.entries(filterData || {}).map(([key, value]) =>
              value ? (
                <Chip
                  key={key}
                  label={`${key.replace(/_/g, ' ')}: ${value}`}
                  size="small"
                  variant="outlined"
                  onDelete={(e) => {
                    e.stopPropagation();
                    setFilterData((prev) => ({ ...(prev || {}), [key]: '' }));
                    // Trigger search automatically on chip delete since it's a minor UI action
                    setTimeout(() => handleClick?.({ ...(filterData || {}), [key]: '' }), 10);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    borderRadius: 1.5,
                    bgcolor: 'rgba(6, 85, 165, 0.04)',
                    color: '#0655A5',
                    borderColor: 'rgba(6, 85, 165, 0.2)',
                    '& .MuiChip-deleteIcon': { fontSize: 14, color: '#0655A5', opacity: 0.7, '&:hover': { opacity: 1 } },
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              ) : null
            )
          )}
        </Box>

        {/* Global Toolbar Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {showImportButton && importar && (
            importMode === 'excel' ? (
              <Button
                size="small"
                variant="text"
                startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
                onClick={() => setModalOpen(true)}
                sx={{ height: 32, fontSize: '0.72rem', textTransform: 'none', fontWeight: 600, color: '#0655A5' }}
              >
                Importar
              </Button>
            ) : (
              // ── Dedicated SyncDialog (importMode === 'sync') ──
              <SyncDialog
                onSync={(modo, payload) => handleImport(modo, payload)}
                {...syncDialogProps}
              />
            )
          )}
          
          {showExportButton && <ExportButton data={data} />}

          <Tooltip title="Personalizar columnas visibles">
            <IconButton
              size="small"
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
              sx={{ color: 'text.secondary', border: '1px solid #e5e7eb', borderRadius: 2 }}
            >
              <FilterListAltIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* ── Advanced Filters Popover ── */}
      <Popover
        open={openAdvanced}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 420,
              mt: 1.5,
              borderRadius: 4,
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Popover Header */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TuneIcon sx={{ fontSize: 20, color: '#0655A5' }} />
              <Typography variant="subtitle2" fontWeight={800} color="#0B1E36">Búsqueda Avanzada</Typography>
            </Box>
            <IconButton onClick={() => setFilterAnchorEl(null)} size="small" sx={{ color: 'text.disabled' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Popover Body (Grid of fields) */}
          <Box
            component="form"
            onSubmit={(e) => { e.preventDefault(); setFilterAnchorEl(null); submitFilters(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'SELECT') {
                e.preventDefault();
                setFilterAnchorEl(null);
                submitFilters();
              }
            }}
            sx={{ p: 2.5, maxHeight: '60vh', overflowY: 'auto' }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {Object.entries(filterData || {})
                .filter(([key]) => visibleFilters.includes(key))
                .map(([key, value]) => {
                  const isDate = key.toUpperCase().includes('FECHA');
                  const isSelect = key === 'ESTATUS' || key === 'DIVISA';
                  let label = key.replace(/_/g, ' ');
                  if (key === 'ESTATUS') label = 'Estado';
                  const isFullWidth = isDate || key === 'PROVEEDOR' || key === 'EMPRESA';

                  const sharedSx = {
                    gridColumn: isFullWidth ? 'span 2' : 'span 1',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff',
                      fontSize: '0.8rem',
                      transition: 'border-color 0.2s',
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#0655A5' },
                      '&.Mui-focused fieldset': { borderColor: '#0655A5' },
                    },
                    '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 700 },
                  };

                  if (isDate) return (
                    <TextField
                      key={key}
                      label={label}
                      type="date"
                      fullWidth
                      size="small"
                      value={value || ''}
                      onChange={(e) => handleDateChangeNative(e.target.value, key)}
                      InputLabelProps={{ shrink: true }}
                      sx={sharedSx}
                    />
                  );

                  if (isSelect) return (
                    <TextField
                      key={key}
                      label={label}
                      fullWidth
                      select
                      size="small"
                      value={value ?? ''}
                      onChange={(e) => setFilterData((prev) => ({ ...(prev || {}), [key]: e.target.value }))}
                      sx={sharedSx}
                      SelectProps={{ MenuProps: { disableRestoreFocus: true } }}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {estatusOptions.map((option) => {
                        const displayMap = {
                          'Proceso': 'Pendiente',
                          'Autorizada': 'Autorizada',
                          'Pagada': 'Pagada',
                          'Cancelada': 'Rechazada',
                          'Cancelada ERP': 'Cancelada',
                          'Vencida': 'Vencida',
                          'Default': 'Default'
                        };
                        const displayLabel = key === 'ESTATUS' ? (displayMap[option] || option) : option;
                        return <MenuItem key={option} value={option}>{displayLabel}</MenuItem>;
                      })}
                    </TextField>
                  );

                  return (
                    <TextField
                      key={key}
                      label={label}
                      placeholder={`Buscar ${label.toLowerCase()}...`}
                      value={value ?? ''}
                      size="small"
                      onChange={(e) => setFilterData((prev) => ({ ...(prev || {}), [key]: e.target.value }))}
                      fullWidth
                      sx={sharedSx}
                    />
                  );
                })}
            </Box>
          </Box>

          {/* Popover Footer */}
          <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9', bgcolor: '#fff', display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<DeleteSweepIcon />}
              onClick={() => { clearAllFilters(); setFilterAnchorEl(null); }}
              sx={{ borderRadius: 2, height: 38, textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: 'text.secondary', fontSize: '0.75rem' }}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={() => { submitFilters(); setFilterAnchorEl(null); }}
              sx={{
                borderRadius: 2,
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
                background: 'linear-gradient(135deg, #0655A5 0%, #054785 100%)',
                boxShadow: '0 4px 12px rgba(6,85,165,0.2)',
              }}
            >
              Aplicar Filtros
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Column Selector Menu (Existing) ── */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        disableRestoreFocus
        slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 220, mt: 1 } } } }
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f1f5f9' }}>
           <Typography variant="overline" fontWeight={800} color="text.disabled">Visibilidad de Columnas</Typography>
        </Box>
        {Object.keys(filterData || {}).map((key) => {
          const checked = visibleFilters.includes(key);
          return (
            <MenuItem
              key={key}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFilter(key); }}
              dense
              sx={{ py: 0.75 }}
            >
              <ListItemIcon>
                <Checkbox edge="start" checked={checked} disableRipple size="small" />
              </ListItemIcon>
              <ListItemText primary={key.replace(/_/g, ' ')} primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: checked ? 700 : 400 }} />
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dirtyRef.current = true;
            setVisibleFilters(stableInitialFilters);
            setFilterMenuAnchor(null);
          }}
          dense
          sx={{ py: 1, color: '#0655A5' }}
        >
          <ListItemIcon>
            <DeleteSweepIcon fontSize="small" sx={{ color: '#0655A5' }} />
          </ListItemIcon>
          <ListItemText primary="Restablecer Valores" primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 800 }} />
        </MenuItem>
      </Menu>

      {/* ── Excel Import Dialog (Existing) ── */}
      {importMode === 'excel' && (
        <Dialog open={isModalOpen} onClose={handleCloseModal} disableAutoFocus disableRestoreFocus keepMounted={false}>
          <DialogTitle sx={{ bgcolor: '#0655A5', color: 'white', fontWeight: 700 }}>Importar archivo Excel</DialogTitle>
          <DialogContent>
            <Paper
              elevation={0}
              {...getRootProps()}
              sx={{
                textAlign: 'center',
                p: 3,
                cursor: 'pointer',
                mt: 1.5,
                border: '2px dashed',
                borderRadius: 2,
                borderColor: 'divider',
                backgroundColor: '#f8fafc',
                transition: 'background .2s, border-color .2s',
                '&:hover': { backgroundColor: '#eff6ff', borderColor: '#0655A5' },
              }}
            >
              <input {...getInputProps()} />
              <AttachFileIcon color="primary" sx={{ fontSize: 48 }} />
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 800 }}>Haz clic o arrastra el archivo aquí</Typography>
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>Formatos permitidos: .xls, .xlsx — Tamaño máx: 15MB</Typography>
              {selectedFileName && <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>Archivo: {selectedFileName}</Typography>}
            </Paper>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.25 }}>
            <Button onClick={handleCloseModal} startIcon={<CloseIcon />}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!isFileProcessed) { setSelectedFileName('Por favor, carga un archivo válido.'); return; }
                setModalOpen(false);
                await handleImport?.();
                setExcelData?.([]);
                setSelectedFileName('No se ha seleccionado archivo.');
                setIsFileProcessed(false);
              }}
              variant="contained"
              startIcon={<AttachFileIcon />}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default React.memo(FilterComponent);

